import { useSetState } from 'ahooks';
import { useMemo, useRef } from 'react';
import { useCommunityInteraction } from '@/app/components/shared/Community';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { useCreationNavigation, useFetch, useMediaMetrics } from '@/app/hooks';
import type { CommunityEnum, ExploreTab, InspirationItem, UpdateInteractionStatusParams } from '@/app/types';
import { getAssetInfo, reportEvent, transformExploreDetail } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { apiGetDetail } from '../apis';
import { GUEST_PROFILE_PREVIEW_PAGE_SIZE, PAGE_SIZE, shouldLimitGuestProfileModules } from '../constant';
import type { CommunityState, InspirationFetchDataResult, UseCommunityParams } from '../types';

export const useFetchData = ({ userId, fetchData }: UseCommunityParams): InspirationFetchDataResult => {
  const navigationDirection = useRef<'prev' | 'next' | null>(null);
  const pathname = usePathname();
  const shouldLimitPreview = shouldLimitGuestProfileModules(userId);
  const pageSize = shouldLimitPreview ? GUEST_PROFILE_PREVIEW_PAGE_SIZE : PAGE_SIZE;
  const [state, setState] = useSetState<CommunityState>({
    openPlanDialog: false,
    page: 1,
    list: [],
    total: 0,
    hasMore: false,
  });

  const { handleEnter, handleLeave, incrementCompleteTimes } = useMediaMetrics({
    callback: (detailStayTime: number, completeTimes: number) => {
      const { type, traceId = '', id = '' } = getAssetInfo(state.selectedAsset) || {};
      type &&
        reportEvent(TRACE_EVENT_NAME[type], {
          [TRACE_EVENT_ID[type]]: id,
          interaction_type: 'view_detail',
          interaction_position: pathname,
          trace_id: traceId,
          detail_staytime: detailStayTime,
          complete_times: completeTimes,
          content_type: 'video',
        });
    },
  });

  const fetchReq = useFetch(
    () =>
      fetchData({
        query: {
          'pager.page': state.page - 1,
          'pager.pagesz': pageSize,
        },
        path: {
          user_id: userId || '',
        },
      }),
    {
      refreshDeps: [state.page],
      onSuccess: ({ data }) => {
        if (data) {
          const { total, list } = data;
          const newList = state.list.concat(list ?? []);
          setState({
            total: shouldLimitPreview ? newList.length : (total ?? 0),
            list: newList,
            hasMore: !shouldLimitPreview && (total ?? 0) > newList.length,
          });

          if (list && navigationDirection.current) {
            handlePageChange(
              navigationDirection.current,
              list.map((item: InspirationItem) => ({
                ...item,
                id:
                  item.image_asset?.id ??
                  item.media_asset?.id ??
                  item.short_film?.id ??
                  item.tutorial?.id ??
                  item.mini_video?.id,
              })),
            );
            navigationDirection.current = null;
          }
        }
      },
    },
  );

  const { run: getDetailInfo } = useFetch(
    (id: string) =>
      apiGetDetail({
        path: {
          id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (data) {
          handleEnter();
          const { type, value } = transformExploreDetail(data) || {};
          type &&
            setState({
              selectedAsset: {
                [type]: value,
              },
            });
        }
      },
    },
  );

  const totalPages = useMemo(() => Math.ceil((state.total ?? 0) / pageSize), [state.total, pageSize]);

  const { isPrevButtonDisabled, isNextButtonDisabled, handleSelectedCreation, handleNavigation, handlePageChange } =
    useCreationNavigation({
      items: state.list.map((item) => ({
        ...item,
        id:
          item.image_asset?.id ??
          item.media_asset?.id ??
          item.short_film?.id ??
          item.tutorial?.id ??
          item.mini_video?.id,
      })),
      page: state.page,
      totalPages,
      loading: fetchReq.loading,
      onPageChange: (page) => setState({ page }),
      onCreationSelect: (creation) => creation.id && getDetailInfo(creation.id),
    });

  const onNavigate = (type: 'prev' | 'next') => {
    handleLeave();
    const direction = handleNavigation(type);
    if (direction) {
      navigationDirection.current = direction;
    }
  };

  /**
   * 灵感/短片/教程 收藏/点赞之后更新状态
   */
  const updateInteractionStatus = ({ id, obj_type, voted, collected }: UpdateInteractionStatusParams) => {
    const type = obj_type as ExploreTab;
    const updatedItems = [...state.list];

    const item = updatedItems.find((item) => item[type]?.id === id);

    if (typeof voted === 'boolean' && item?.[type]?.react_info) {
      item[type].react_info = {
        ...item[type].react_info,
        vote_count: (item[type].react_info?.vote_count ?? 0) + (voted ? 1 : -1),
        voted,
      };
    }
    if (typeof collected === 'boolean' && item?.[type]?.react_info) {
      item[type].react_info = {
        ...item[type].react_info,
        collect_count: (item[type].react_info?.collect_count ?? 0) + (collected ? 1 : -1),
        collected,
      };
    }
    setState({ list: updatedItems });
  };

  const { vote, unVote, collect, unCollect } = useCommunityInteraction({
    onInteractionStatusChange: ({ id, obj_type, voted, collected }) => {
      updateInteractionStatus({ id, obj_type: obj_type as CommunityEnum, voted, collected });
    },
  });

  return {
    state,
    setState,
    isPrevButtonDisabled,
    isNextButtonDisabled,
    onNavigate,
    handleDetail: handleSelectedCreation,
    vote,
    unVote,
    collect,
    unCollect,
    getDetailInfo,
    incrementCompleteTimes,
    handleLeave,
    fetchReq,
  };
};
